import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Users, BookOpen, TrendingUp, Award, GraduationCap, Star,
} from 'lucide-react';

const Reports = () => {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);

  useEffect(() => {
    axios.get('/api/admin/reports')
      .then(({ data: res }) => { if (res.success) setData(res); })
      .catch(() => setError('Impossible de charger les rapports'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="min-h-full bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-full bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <p className="text-red-500">{error || 'Erreur de chargement'}</p>
      </div>
    );
  }

  const { stats, roleDistribution, enrollmentsByMonth, topCourses } = data;

  const maxEnrollment = Math.max(...enrollmentsByMonth.map(m => m.count), 1);

  return (
    <div className="min-h-full bg-gray-50 dark:bg-gray-900">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-1">Rapports & Analyses</h1>
        <p className="text-gray-500 dark:text-gray-400 mb-8">Vue d'ensemble de l'activité de la plateforme</p>

        {/* Stats globales */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
          {[
            { icon: Users,        label: 'Utilisateurs',   value: stats.totalUsers,        color: 'bg-blue-500' },
            { icon: BookOpen,     label: 'Cours total',    value: stats.totalCourses,       color: 'bg-indigo-500' },
            { icon: TrendingUp,   label: 'Cours publiés',  value: stats.publishedCourses,   color: 'bg-green-500' },
            { icon: GraduationCap,label: 'Inscriptions',   value: stats.totalEnrollments,   color: 'bg-yellow-500' },
            { icon: Award,        label: 'Certificats',    value: stats.totalCertificates,  color: 'bg-purple-500' },
          ].map(s => (
            <div key={s.label} className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4 flex items-center gap-3">
              <div className={`w-10 h-10 ${s.color} rounded-lg flex items-center justify-center shrink-0`}>
                <s.icon className="w-5 h-5 text-white" />
              </div>
              <div>
                <div className="text-xl font-bold text-gray-900 dark:text-white">{s.value}</div>
                <div className="text-xs text-gray-500 dark:text-gray-400">{s.label}</div>
              </div>
            </div>
          ))}
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Inscriptions par mois */}
          <div className="lg:col-span-2 bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">
              Inscriptions — 6 derniers mois
            </h2>
            <div className="flex items-end gap-3 h-40">
              {enrollmentsByMonth.map(m => (
                <div key={m.label} className="flex-1 flex flex-col items-center gap-1">
                  <span className="text-xs font-medium text-gray-700 dark:text-gray-300">{m.count}</span>
                  <div
                    className="w-full bg-primary rounded-t transition-all"
                    style={{ height: `${Math.round((m.count / maxEnrollment) * 100)}%`, minHeight: m.count > 0 ? '4px' : '2px' }}
                  />
                  <span className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">{m.label}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Distribution des rôles */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-5">
              Répartition des rôles
            </h2>
            <div className="space-y-4">
              {[
                { key: 'student',    label: 'Étudiants',    color: 'bg-green-500' },
                { key: 'instructor', label: 'Instructeurs', color: 'bg-blue-500' },
                { key: 'admin',      label: 'Admins',       color: 'bg-red-500' },
              ].map(r => {
                const count = roleDistribution[r.key] || 0;
                const pct   = stats.totalUsers > 0 ? Math.round((count / stats.totalUsers) * 100) : 0;
                return (
                  <div key={r.key}>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-gray-700 dark:text-gray-300">{r.label}</span>
                      <span className="font-medium text-gray-900 dark:text-white">{count} ({pct}%)</span>
                    </div>
                    <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                      <div className={`${r.color} h-2 rounded-full transition-all`} style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Top cours */}
          <div className="lg:col-span-3 bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-5">
              Top cours par inscriptions
            </h2>
            {topCourses.length === 0 ? (
              <p className="text-center py-6 text-gray-500">Aucun cours publié</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200 dark:border-gray-700">
                      <th className="text-left py-2 px-3 font-medium text-gray-600 dark:text-gray-400">#</th>
                      <th className="text-left py-2 px-3 font-medium text-gray-600 dark:text-gray-400">Cours</th>
                      <th className="text-left py-2 px-3 font-medium text-gray-600 dark:text-gray-400 hidden sm:table-cell">Catégorie</th>
                      <th className="text-right py-2 px-3 font-medium text-gray-600 dark:text-gray-400">Inscrits</th>
                      <th className="text-right py-2 px-3 font-medium text-gray-600 dark:text-gray-400 hidden sm:table-cell">Note</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                    {topCourses.map((course, i) => (
                      <tr key={course.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                        <td className="py-3 px-3 text-gray-400 font-medium">{i + 1}</td>
                        <td className="py-3 px-3 font-medium text-gray-900 dark:text-white">{course.title}</td>
                        <td className="py-3 px-3 text-gray-500 hidden sm:table-cell">{course.category || '—'}</td>
                        <td className="py-3 px-3 text-right font-semibold text-primary">{course.enrollments_count || 0}</td>
                        <td className="py-3 px-3 text-right hidden sm:table-cell">
                          {course.average_rating ? (
                            <span className="flex items-center justify-end gap-1 text-yellow-500">
                              <Star className="w-3.5 h-3.5 fill-yellow-500" />
                              {Number(course.average_rating).toFixed(1)}
                            </span>
                          ) : <span className="text-gray-400">—</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Reports;
